import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AssignmentTurnedIn, Check, Close } from '@mui/icons-material';

import { IKiTaskPayload, IKiTaskProposal } from '../Interfaces/IKi';
import { ITaskFullObject } from '../Interfaces/ITask';
import {
  useGetTaskPriority,
  useGetTaskType,
  useWriteTaskFullObject,
} from '../hooks/reactQuerys/TaskQueries';

interface IProps {
  proposal: IKiTaskProposal;
}

/**
 * Vorschlagskarte für eine Aufgabe (#1336).
 *
 * Die KI hat die Aufgabe **vorbereitet**, nicht angelegt. Geschrieben wird
 * erst hier, mit der Sitzung des Benutzers über den regulären
 * `POST /task/fullObject` — dieselbe Rechteprüfung wie in der Aufgabenmaske,
 * und im Protokoll steht anschließend eine ganz gewöhnliche Aufgabenanlage
 * durch diese Person.
 *
 * Die Zuständigen stehen als Klarnamen da und nicht als id: eine Verwechslung
 * soll vor dem Klick auffallen, und an einer „7" sieht man sie nicht.
 */
export const KiTaskProposalCard: React.FC<IProps> = ({ proposal }) => {
  const { taskTypes } = useGetTaskType();
  const { taskPriorities } = useGetTaskPriority();
  const { writeTaskFullObject, isPendingWriteTaskFullObject } =
    useWriteTaskFullObject();

  const [payload, setPayload] = useState<IKiTaskPayload>(proposal.Payload);
  const [state, setState] = useState<'open' | 'done' | 'dismissed'>('open');
  const [error, setError] = useState<string | null>(null);

  if (state === 'dismissed') {
    return null;
  }

  if (state === 'done') {
    return (
      <Alert severity='success' icon={<AssignmentTurnedIn fontSize='inherit' />}>
        Aufgabe „{payload.Title}" wurde angelegt.
      </Alert>
    );
  }

  const set = <K extends keyof IKiTaskPayload>(key: K, value: IKiTaskPayload[K]) =>
    setPayload(current => ({ ...current, [key]: value }));

  async function handleCreate() {
    setError(null);

    try {
      await writeTaskFullObject(payload as unknown as ITaskFullObject);
      setState('done');
    } catch (exception) {
      // Die Meldung des Servers nennt den Grund (Pflichtfeld, Berechtigung) —
      // die gehört dem Benutzer gezeigt, nicht verschluckt.
      const detail =
        exception instanceof Error && exception.message
          ? exception.message.replace(/^\d{3} [^:]+: /, '')
          : '';

      setError(detail !== '' ? detail : 'Die Aufgabe konnte nicht angelegt werden.');
    }
  }

  const zustaendige = proposal.Display.Zustaendige ?? [];

  return (
    <Paper variant='outlined' sx={{ p: 1.5, borderColor: 'primary.light' }}>
      <Stack spacing={1.5}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <AssignmentTurnedIn fontSize='small' color='primary' />
          <Typography variant='subtitle2'>Aufgabe anlegen</Typography>
        </Stack>

        <TextField
          label='Titel'
          size='small'
          fullWidth
          value={payload.Title}
          onChange={event => set('Title', event.target.value)}
        />

        <TextField
          label='Beschreibung'
          size='small'
          fullWidth
          multiline
          maxRows={4}
          value={payload.Body}
          onChange={event => set('Body', event.target.value)}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            label='Frist'
            type='date'
            size='small'
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={payload.Deadline_at}
            onChange={event => set('Deadline_at', event.target.value)}
          />
          <TextField
            label='Art'
            select
            size='small'
            fullWidth
            value={payload.idTaskType}
            onChange={event => set('idTaskType', Number(event.target.value))}
          >
            {(taskTypes ?? []).map(type => (
              <MenuItem key={`kitask-type-${type.idTaskType}`} value={type.idTaskType}>
                {type.TaskType}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label='Priorität'
            select
            size='small'
            fullWidth
            value={payload.idTaskPriority}
            onChange={event => set('idTaskPriority', Number(event.target.value))}
          >
            {(taskPriorities ?? []).map(prio => (
              <MenuItem
                key={`kitask-prio-${prio.idTaskPriority}`}
                value={prio.idTaskPriority}
              >
                {prio.TaskPriority}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Box>
          <Typography variant='caption' color='text.secondary'>
            Zuständig
          </Typography>
          <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap sx={{ mt: 0.5 }}>
            {zustaendige.length > 0 ? (
              zustaendige.map(name => (
                <Chip key={`kitask-user-${name}`} size='small' label={name} />
              ))
            ) : (
              <Chip size='small' label='niemand' variant='outlined' />
            )}
            {proposal.Display.Vorgang !== null &&
              proposal.Display.Vorgang !== undefined && (
              <Chip
                size='small'
                color='primary'
                variant='outlined'
                label={`Vorgang: ${proposal.Display.Vorgang}`}
              />
            )}
            {proposal.Display.Mandant !== null &&
              proposal.Display.Mandant !== undefined && (
              <Chip
                size='small'
                color='primary'
                variant='outlined'
                label={`Mandant: ${proposal.Display.Mandant}`}
              />
            )}
          </Stack>
        </Box>

        {proposal.Hinweise.length > 0 && (
          // Was das Werkzeug beim Normalisieren angepasst hat. Stillschweigend
          // korrigieren wäre schlechter: dann wundert sich jemand später über
          // eine Frist, die er nie genannt hat.
          <Alert severity='info' sx={{ py: 0 }}>
            <Stack component='ul' sx={{ m: 0, pl: 2 }}>
              {proposal.Hinweise.map(hinweis => (
                <Typography component='li' variant='caption' key={hinweis}>
                  {hinweis}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {error !== null && <Alert severity='error'>{error}</Alert>}

        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button
            size='small'
            color='inherit'
            startIcon={<Close />}
            disabled={isPendingWriteTaskFullObject}
            onClick={() => setState('dismissed')}
          >
            Verwerfen
          </Button>
          <Button
            size='small'
            variant='contained'
            startIcon={<Check />}
            disabled={isPendingWriteTaskFullObject || payload.Title.trim() === ''}
            onClick={() => void handleCreate()}
          >
            Anlegen
          </Button>
        </Stack>

        <Typography variant='caption' color='text.secondary'>
          Weitere Felder und die Zuständigkeit lassen sich nach dem Anlegen in
          der Aufgabe ändern.
        </Typography>
      </Stack>
    </Paper>
  );
};
